import { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import { activeBrand } from "../brands/index.js";
import { colorsSemantic } from "../tokens/colors-semantic.js";
import { colorsFoundation } from "../tokens/colors-foundation.js";
import { hexToRgba } from "../utils/colors.js";
import { dimensions } from "../tokens/dimensions.js";

// Primary-role accent (rose in the generic skin) — drives the badge + focus feel.
const accent = colorsFoundation.key.blue[600];

// Login marketing copy is skinnable via the active brand's `auth` block, with the
// generic strings as fallback. The left panel background is skinnable via
// `assets.background`; when absent, the solid dark sidebar colour is used.
const authCopy = activeBrand.auth ?? {};
const badgeText = authCopy.badge ?? "Digitale Schadenmeldung";
const headlineLines = Array.isArray(authCopy.headline)
  ? authCopy.headline
  : (authCopy.headline ?? "Schäden melden.\nEinfach. Sicher.").split("\n");
const subtext =
  authCopy.subtext ??
  "Ihr digitales Portal zur schnellen und transparenten Abwicklung von Versicherungsschäden – vom Eingang bis zur Auszahlung.";
const brandBackground = activeBrand.assets?.background;

/**
 * AuthLayout - Split-screen authentication shell (matches the Klick-Dummy).
 *
 * Left: dark brand panel with logo, marketing headline and legal footer.
 * Right: white panel that hosts the auth form (login, reset, 2FA, …).
 *
 * The left panel collapses on small screens so the form takes the full width.
 * All colours come from the active skin (theme/brands) so it stays skinnable.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Form content shown in the right panel
 * @param {React.ReactNode} props.topSlot - Optional full-width strip above the split (e.g. environment banner)
 */
export default function AuthLayout({ children, topSlot = null }) {
  const year = new Date().getFullYear();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: colorsFoundation.neutral.white,
      }}
    >
      {topSlot}

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* ── Left brand panel ── */}
        <Box
          sx={{
            flex: "0 0 50%",
            minWidth: 0,
            boxSizing: "border-box",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            bgcolor: colorsSemantic.background.sidebar,
            // Optional per-skin background photo, dimmed so the light text stays
            // legible. Falls back to the solid sidebar colour above when unset.
            ...(brandBackground && {
              backgroundImage: `linear-gradient(rgba(17,24,39,0.72), rgba(17,24,39,0.72)), url(${brandBackground})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }),
            p: "56px",
          }}
        >
          {/* Brand mark */}
          <Box sx={{ display: "flex", alignItems: "center", gap: dimensions.md }}>
            <Box
              component="img"
              src={activeBrand.assets.logo}
              alt={activeBrand.assets.logoAlt}
              sx={{ width: 34, height: 34, display: "block" }}
            />
            {activeBrand.features?.showSidebarTitle !== false && (
              <Typography
                component="span"
                sx={{
                  color: colorsFoundation.neutral.white,
                  fontSize: "17px",
                  fontWeight: 600,
                  letterSpacing: "-0.2px",
                }}
              >
                {activeBrand.productName}
              </Typography>
            )}
          </Box>

          {/* Headline block */}
          <Box>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                mb: "24px",
                px: "14px",
                py: "4px",
                borderRadius: "20px",
                bgcolor: hexToRgba(accent, 0.15),
                border: `1px solid ${hexToRgba(accent, 0.28)}`,
                color: colorsFoundation.key.blue[300],
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {badgeText}
            </Box>
            <Typography
              sx={{
                color: colorsFoundation.neutral.white,
                fontSize: "40px",
                fontWeight: 700,
                lineHeight: 1.16,
                letterSpacing: "-0.6px",
                mb: "18px",
              }}
            >
              {headlineLines.map((line, i) => (
                <Fragment key={i}>
                  {i > 0 && <br />}
                  {line}
                </Fragment>
              ))}
            </Typography>
            <Typography
              sx={{
                color: colorsFoundation.gray[400],
                fontSize: "15px",
                lineHeight: 1.7,
                maxWidth: "420px",
              }}
            >
              {subtext}
            </Typography>
          </Box>

          {/* Legal footer */}
          <Typography sx={{ fontSize: "12px", color: "#4b5563" }}>
            © {year} {activeBrand.productName} &nbsp;·&nbsp; Datenschutz
            &nbsp;·&nbsp; Impressum
          </Typography>
        </Box>

        {/* ── Right form panel ── */}
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "0 0 50%" },
            minWidth: 0,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: colorsFoundation.neutral.white,
            p: { xs: dimensions["3xl"], md: "64px" },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "360px" }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
}
